import { useState, useEffect, useCallback } from 'react';
import {
  WorkOrderCost,
  Quotation,
  Bill,
  CostLineItem,
  CreateCostDto,
  CreateQuotationDto,
  CreateBillDto,
} from '../types/billing';
import { billingService } from '../services/billing/billingService';

export function useCostEntry(workOrderId?: string, complaintId?: string, vendorId?: string) {
  const [cost, setCost] = useState<WorkOrderCost | null>(null);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadCost = useCallback(async () => {
    if (!workOrderId) return;
    setIsLoading(true);
    const res = await billingService.getCostByWorkOrder(workOrderId);
    if (res.success) setCost(res.data);
    setIsLoading(false);
  }, [workOrderId]);

  const loadQuotations = useCallback(async () => {
    if (!workOrderId) return;
    const res = await billingService.getQuotations({ work_order_id: workOrderId });
    if (res.success) setQuotations(res.data);
  }, [workOrderId]);

  const loadBills = useCallback(async () => {
    if (!workOrderId) return;
    const res = await billingService.getBills({ work_order_id: workOrderId });
    if (res.success) setBills(res.data);
  }, [workOrderId]);

  useEffect(() => {
    loadCost();
    loadQuotations();
    loadBills();
  }, [loadCost, loadQuotations, loadBills]);

  const saveCost = useCallback(async (
    materials: CostLineItem[],
    labor: CostLineItem[],
    service: CostLineItem[],
    other: CostLineItem[],
    taxRate: number,
  ) => {
    if (!workOrderId || !complaintId || !vendorId) return { success: false };
    setIsLoading(true);
    const dto: CreateCostDto = {
      work_order_id: workOrderId,
      complaint_id: complaintId,
      vendor_id: vendorId,
      materials,
      labor,
      service,
      other,
      tax_rate: taxRate,
    };
    const res = await billingService.createOrUpdateCost(dto);
    if (res.success) setCost(res.data);
    setIsLoading(false);
    return res;
  }, [workOrderId, complaintId, vendorId]);

  const removeLineItem = useCallback(async (itemId: string) => {
    if (!cost) return { success: false };
    setIsLoading(true);
    const res = await billingService.deleteCostLineItem(cost.cost_id, itemId);
    if (res.success) setCost(res.data);
    setIsLoading(false);
    return res;
  }, [cost]);

  const submitQuotation = useCallback(async (items: CostLineItem[], validityDays: number, notes: string) => {
    if (!workOrderId || !vendorId) return { success: false };
    const dto: CreateQuotationDto = {
      work_order_id: workOrderId,
      vendor_id: vendorId,
      items,
      validity_days: validityDays,
      notes,
    };
    const res = await billingService.submitQuotation(dto);
    if (res.success) {
      setQuotations(prev => [res.data, ...prev]);
    }
    return res;
  }, [workOrderId, vendorId]);

  const approveQuotation = useCallback(async (quotationId: string) => {
    const res = await billingService.approveQuotation(quotationId, 'Current User');
    if (res.success) {
      setQuotations(prev => prev.map(q => q.quotation_id === quotationId ? res.data : q));
    }
    return res;
  }, []);

  const rejectQuotation = useCallback(async (quotationId: string, reason: string) => {
    const res = await billingService.rejectQuotation(quotationId, reason);
    if (res.success) {
      setQuotations(prev => prev.map(q => q.quotation_id === quotationId ? res.data : q));
    }
    return res;
  }, []);

  const generateBill = useCallback(async (quotationId: string, taxRate: number, dueDate: string) => {
    if (!workOrderId || !complaintId || !vendorId) return { success: false };
    const quotation = quotations.find(q => q.quotation_id === quotationId);
    if (!quotation) return { success: false };

    const dto: CreateBillDto = {
      work_order_id: workOrderId,
      complaint_id: complaintId,
      vendor_id: vendorId,
      quotation_id: quotationId,
      line_items: quotation.items,
      tax_rate: taxRate,
      due_date: dueDate,
    };
    const res = await billingService.generateBill(dto);
    if (res.success) {
      setBills(prev => [res.data, ...prev]);
    }
    return res;
  }, [workOrderId, complaintId, vendorId, quotations]);

  const markBillPaid = useCallback(async (billId: string) => {
    const ref = `PAY-${Date.now()}`;
    const res = await billingService.markBillPaid(billId, ref);
    if (res.success) {
      setBills(prev => prev.map(b => b.bill_id === billId ? res.data : b));
    }
    return res;
  }, []);

  const refresh = useCallback(() => {
    loadCost();
    loadQuotations();
    loadBills();
  }, [loadCost, loadQuotations, loadBills]);

  return {
    cost,
    quotations,
    bills,
    isLoading,
    saveCost,
    removeLineItem,
    submitQuotation,
    approveQuotation,
    rejectQuotation,
    generateBill,
    markBillPaid,
    refresh,
  };
}
